import type { AuthScheme } from './identity';
import { schemaFields } from './schema';
import { CanonicalSchema, GroupTemplate } from './types';

/**
 * Reading an API catalogue — the endpoints vendors are told to call — out of its own documentation.
 *
 * The paths, the shapes and the examples already exist, written down and kept current by the team
 * that owns them. Retyping them here would make a second copy that is wrong the first time anyone
 * changes the real one, so nothing about any particular API is written into this platform: it is
 * pointed at a document and reads what is there.
 *
 * Two documents are understood, because both are in use:
 *
 *   **OpenAPI** — typed fields, so the target tree in the mapper is exact.
 *   **Insomnia export** — keeps the *named* variants one path can have (Invoice, Return, Void and
 *   Exchange all POST the same address and differ only in what is in the body) and carries real
 *   example payloads, which become the samples a mapping is tested against.
 *
 * Importing both is normal: the same group takes named endpoints from one and field types from
 * the other.
 */

export type CatalogSource = 'openapi' | 'insomnia';

export interface CatalogEndpoint {
  name: string;
  slug: string;
  method: 'GET' | 'POST' | 'PUT';
  /** The public path a vendor calls, with `{placeholders}` left as they are written. */
  path: string;
  description?: string;
  /** The body as JSON Schema, when the document describes one. */
  body?: Record<string, unknown>;
  /** The docs' own examples, by the names the docs give them, pretty printed. */
  examples?: CatalogExample[];
  /** True when the body is a list — one request carries many documents. */
  isBatch?: boolean;
  /** Header names the document says authenticate this call, as the document spells them. */
  auth?: string[];
}

/** One example request from the docs, such as "Bill Sync - Return". */
export interface CatalogExample {
  name: string;
  payload: string;
}

export interface CatalogGroup {
  id: string;
  name: string;
  description?: string;
  endpoints: CatalogEndpoint[];
}

export interface Catalog {
  source: CatalogSource;
  title?: string;
  groups: CatalogGroup[];
}

const slugOf = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);

const METHODS = new Set(['GET', 'POST', 'PUT']);

const asMethod = (value: unknown): CatalogEndpoint['method'] | undefined => {
  const method = String(value ?? '').toUpperCase();

  return METHODS.has(method) ? (method as CatalogEndpoint['method']) : undefined;
};

/* ── OpenAPI ─────────────────────────────────────────────────────────────────────────────── */

/** Follows `$ref` into the document's own components, giving up rather than looping forever. */
const resolver = (document: Record<string, unknown>) => {
  const components = ((document.components as Record<string, unknown>)?.schemas ?? {}) as Record<string, unknown>;

  return function resolve(node: unknown, depth = 0): Record<string, unknown> {
    if (!node || typeof node !== 'object' || depth > 20) return {};
    const reference = (node as Record<string, unknown>).$ref;
    if (typeof reference !== 'string') return node as Record<string, unknown>;
    const name = reference.split('/').pop() ?? '';

    return resolve(components[name], depth + 1);
  };
};

/**
 * The same schema with every `$ref` replaced by what it points at.
 *
 * The mapper needs a tree it can walk, and a reference it cannot follow shows as an empty branch
 * — a field the vendor sends with nowhere to put it.
 */
const inlined = (
  node: unknown,
  resolve: (value: unknown, depth?: number) => Record<string, unknown>,
  depth = 0,
): Record<string, unknown> => {
  if (depth > 12) return {};
  const schema = resolve(node);
  const copy: Record<string, unknown> = { ...schema };
  delete copy.$ref;

  if (schema.properties && typeof schema.properties === 'object') {
    copy.properties = Object.fromEntries(
      Object.entries(schema.properties as Record<string, unknown>).map(([name, child]) => [
        name,
        inlined(child, resolve, depth + 1),
      ]),
    );
  }
  if (schema.items) copy.items = inlined(schema.items, resolve, depth + 1);

  return copy;
};

/** `apiKey` is what an operation lists; `api-key` is the header a vendor actually sends. */
const headerNames = (document: Record<string, unknown>) => {
  const schemes = ((document.components as Record<string, unknown>)?.securitySchemes ?? {}) as Record<
    string,
    Record<string, unknown>
  >;

  return (security: unknown): string[] | undefined =>
    Array.isArray(security)
      ? security.flatMap((entry: Record<string, unknown>) =>
          Object.keys(entry ?? {}).map((name) =>
            schemes[name]?.in === 'header' && typeof schemes[name]?.name === 'string'
              ? String(schemes[name].name)
              : name,
          ),
        )
      : undefined;
};

/** `POS` is ambiguous where the document keeps a V1 and a V2 of its API; `V2 · POS` is not. */
const tagNames = (document: Record<string, unknown>) => {
  const sections = new Map<string, string>();
  for (const section of (document['x-tagGroups'] ?? []) as Record<string, unknown>[]) {
    for (const tag of (section?.tags ?? []) as string[]) sections.set(tag, String(section.name));
  }

  return (tag: string): string => (sections.has(tag) ? `${sections.get(tag)} · ${tag}` : tag);
};

/** The named examples of one request body, following `$ref`s into `components/examples`. */
const examplesOf = (json: Record<string, unknown> | undefined, document: Record<string, unknown>): CatalogExample[] => {
  if (!json) return [];
  const shared = ((document.components as Record<string, unknown>)?.examples ?? {}) as Record<string, unknown>;
  const named = (json.examples ?? {}) as Record<string, Record<string, unknown>>;
  const found = Object.entries(named).flatMap(([name, raw]) => {
    const reference = typeof raw?.$ref === 'string' ? raw.$ref.split('/').pop() : undefined;
    const example = (reference ? shared[reference] : raw) as Record<string, unknown> | undefined;
    return example && example.value !== undefined
      ? [{ name: String(example.summary ?? name), payload: JSON.stringify(example.value, null, 2) }]
      : [];
  });
  if (found.length === 0 && json.example !== undefined)
    found.push({ name: 'Example', payload: JSON.stringify(json.example, null, 2) });
  return found;
};

const openApiCatalog = (document: Record<string, unknown>): Catalog => {
  const resolve = resolver(document);
  const headersOf = headerNames(document);
  const nameOf = tagNames(document);
  const paths = (document.paths ?? {}) as Record<string, Record<string, unknown>>;
  const groups = new Map<string, CatalogGroup>();

  for (const [path, operations] of Object.entries(paths)) {
    for (const [verb, raw] of Object.entries(operations)) {
      const method = asMethod(verb);
      if (!method || !raw || typeof raw !== 'object') continue;
      const operation = raw as Record<string, unknown>;

      const tag = nameOf((operation.tags as string[] | undefined)?.[0] ?? 'Endpoints');
      const name = String(operation.summary ?? operation.operationId ?? `${method} ${path}`);
      const content = ((operation.requestBody as Record<string, unknown>)?.content ?? {}) as Record<
        string,
        Record<string, unknown>
      >;
      const json = content['application/json'];
      const body = json?.schema ? inlined(json.schema, resolve) : undefined;
      const examples = examplesOf(json, document);
      const isBatch = body?.type === 'array';

      const group = groups.get(tag) ?? { id: slugOf(tag), name: tag, endpoints: [] };
      group.endpoints.push({
        name,
        slug: slugOf(`${name}-${path}`),
        method,
        path: numbersAsPlaceholders(path),
        description: typeof operation.description === 'string' ? operation.description : undefined,
        /* A batch endpoint's shape is the shape of one document in it, which is what is mapped. */
        body: isBatch ? inlined(body?.items, resolve) : body,
        isBatch: isBatch || undefined,
        ...(examples.length ? { examples } : {}),
        auth: headersOf(operation.security ?? document.security),
      });
      groups.set(tag, group);
    }
  }

  return {
    source: 'openapi',
    title: String(((document.info as Record<string, unknown>)?.title as string) ?? 'API'),
    groups: [...groups.values()],
  };
};

/* ── Insomnia ────────────────────────────────────────────────────────────────────────────── */

/**
 * A number written into an address is an example of a value that changes per call, so it becomes
 * a placeholder named after the word before it: `/customer/9597915549` is `/customer/{customerId}`.
 */
export const numbersAsPlaceholders = (path: string): string => {
  const taken = new Set([...path.matchAll(/\{([^}]+)\}/g)].map((match) => match[1]));
  const segments = path.split('/');
  return segments
    .map((segment, index) => {
      if (!/^\d+$/.test(segment)) return segment;
      const before = segments[index - 1] ?? '';
      const word = /^[A-Za-z][A-Za-z0-9]*$/.test(before) ? before : 'value';
      const base = /Id$/.test(word) ? word : `${word}Id`;
      let name = base;
      for (let count = 2; taken.has(name); count++) name = `${base}${count}`;
      taken.add(name);
      return `{${name}}`;
    })
    .join('/');
};

/** `{{ baseUrl }}/v2/bills` is the address with the environment's base still in it. */
const pathOf = (url: string): string | undefined => {
  const withoutBase = url
    .trim()
    /* the base address, however it was written: {{ baseUrl }}, {{ _.webApiUrl }}, {{ccUrl}} */
    .replace(/^\{\{[^}]*\}\}/, '')
    .replace(/^https?:\/\/[^/]+/, '')
    /* a variable inside the path is a placeholder, written the way every other document writes it */
    .replace(/\{\{\s*_?\.?\s*\[?'?([A-Za-z0-9_-]+)'?\]?\s*\}\}/g, '{$1}')
    .replace(/\/:([A-Za-z0-9_]+)/g, '/{$1}')
    .trim();

  if (!withoutBase.startsWith('/')) return undefined;
  return numbersAsPlaceholders(withoutBase.split('?')[0]);
};

/** A template variable is not a value: the sample keeps the shape and loses the placeholder. */
const withoutTemplates = (text: string): string =>
  text.replace(/\{\{\s*_?\.?\s*\[?'?([A-Za-z0-9_-]+)'?\]?\s*\}\}/g, '$1');

/** What a real payload says the shape is, when nothing typed it for us. */
const shapeOf = (value: unknown, depth = 0): Record<string, unknown> => {
  if (depth > 8 || value === null || value === undefined) return {};
  if (Array.isArray(value)) return { type: 'array', items: shapeOf(value[0], depth + 1) };
  if (typeof value === 'object')
    return {
      type: 'object',
      properties: Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([name, child]) => [name, shapeOf(child, depth + 1)]),
      ),
    };
  if (typeof value === 'number') return { type: Number.isInteger(value) ? 'integer' : 'number' };
  if (typeof value === 'boolean') return { type: 'boolean' };

  return { type: 'string' };
};

/** "Bill Sync - Invoice" and "Bill Sync - Return" are both "Bill Sync". */
const sharedName = (names: string[]): string => {
  const words = names.map((name) => name.split(/\s+/));
  const common: string[] = [];
  for (let index = 0; words.every((list) => list[index] !== undefined && list[index] === words[0][index]); index++)
    common.push(words[0][index]);
  const joined = common.join(' ').replace(/[\s\-–·:|]+$/, '');
  return joined || names[0];
};

/**
 * Requests on the same method and address are one endpoint with several examples: Invoice, Return,
 * Exchange and Void all POST `/v2/bills`, and what differs is only what is in the body.
 */
const oneEndpointPerAddress = (endpoints: CatalogEndpoint[]): CatalogEndpoint[] => {
  const byAddress = new Map<string, CatalogEndpoint[]>();
  for (const endpoint of endpoints) {
    const key = `${endpoint.method} ${endpoint.path}`;
    byAddress.set(key, [...(byAddress.get(key) ?? []), endpoint]);
  }
  return [...byAddress.values()].map((same) => {
    if (same.length === 1) return same[0];
    const name = sharedName(same.map((endpoint) => endpoint.name));
    return {
      ...same[0],
      name,
      slug: slugOf(`${name}-${same[0].path}`),
      body: same.find((endpoint) => endpoint.body)?.body,
      isBatch: same.some((endpoint) => endpoint.isBatch) || undefined,
      examples: same.flatMap((endpoint) => endpoint.examples ?? []),
      auth: same.find((endpoint) => endpoint.auth?.length)?.auth,
    };
  });
};

const insomniaCatalog = (document: Record<string, unknown>): Catalog => {
  const resources = (document.resources ?? []) as Record<string, unknown>[];
  const folders = new Map(
    resources.filter((item) => item._type === 'request_group').map((item) => [String(item._id), String(item.name)]),
  );
  const groups = new Map<string, CatalogGroup>();

  for (const resource of resources) {
    if (resource._type !== 'request') continue;
    const method = asMethod(resource.method);
    const path = pathOf(String(resource.url ?? ''));
    if (!method || !path) continue;

    const name = String(resource.name ?? `${method} ${path}`);
    const folder = folders.get(String(resource.parentId)) ?? 'Endpoints';
    const text = String((resource.body as Record<string, unknown>)?.text ?? '').trim();

    let sample: string | undefined;
    let body: Record<string, unknown> | undefined;
    let isBatch = false;
    if (text) {
      try {
        const parsed: unknown = JSON.parse(withoutTemplates(text));
        isBatch = Array.isArray(parsed);
        sample = JSON.stringify(parsed, null, 2);
        body = shapeOf(isBatch ? (parsed as unknown[])[0] : parsed);
      } catch {
        /* a body that is not JSON tells us nothing about the shape, and is not a sample */
      }
    }

    const headers = (resource.headers ?? []) as Record<string, unknown>[];
    const group = groups.get(folder) ?? { id: slugOf(folder), name: folder, endpoints: [] };
    group.endpoints.push({
      name,
      slug: slugOf(`${name}-${path}`),
      method,
      path,
      body,
      ...(sample ? { examples: [{ name, payload: sample }] } : {}),
      isBatch: isBatch || undefined,
      auth: headers
        .map((header) => String(header.name ?? ''))
        .filter((header) => /^(api-key|x-tenant-id|x-tenant-token|authorization)$/i.test(header)),
    });
    groups.set(folder, group);
  }

  return {
    source: 'insomnia',
    title: 'Imported collection',
    groups: [...groups.values()].map((group) => ({ ...group, endpoints: oneEndpointPerAddress(group.endpoints) })),
  };
};

/* ── the door ────────────────────────────────────────────────────────────────────────────── */

export const readCatalog = (document: unknown): Catalog => {
  if (!document || typeof document !== 'object') throw new Error('That document is not an API description.');
  const root = document as Record<string, unknown>;

  if (root.paths && typeof root.paths === 'object') return openApiCatalog(root);
  if (Array.isArray(root.resources)) return insomniaCatalog(root);

  throw new Error('That is neither an OpenAPI description nor an exported collection.');
};

/* ── turning a catalogue into configuration ──────────────────────────────────────────────── */

export interface CatalogSchema extends CanonicalSchema {
  /** The endpoint this shape belongs to, so a route knows which schema is its target. */
  endpointSlug: string;
}

/**
 * The canonical schema for one endpoint: the shape Casa's side expects, which is the target the
 * mapper fills in. `required` is left empty on purpose — the document marks nothing as required,
 * so what must be present is a decision made here rather than one imported.
 */
export const catalogSchema = (group: CatalogGroup, endpoint: CatalogEndpoint, version = 1): CatalogSchema => {
  const body = endpoint.body ?? { type: 'object', properties: {} };

  return {
    id: `${group.id}-${endpoint.slug}-${version}`,
    code: `${group.id}-${endpoint.slug}`,
    version,
    title: `${endpoint.name} — what the destination expects`,
    fields: schemaFields(body),
    jsonSchema: body,
    endpointSlug: endpoint.slug,
  };
};

/** Route groups and target schemas, ready to be saved, for the groups that were ticked. */
export interface CatalogImport {
  templates: GroupTemplate[];
  schemas: CanonicalSchema[];
  /** One per way of signing in the document uses, so each group can name its own. */
  authSchemes: AuthScheme[];
}

const has = (headers: string[], name: string) => headers.some((header) => header.toLowerCase() === name);

/**
 * How a vendor signs in, read from the headers the document names. A call that names none — the
 * ones that carry their token in the address — has no scheme a header can check.
 */
export const catalogSignIn = (headers: string[] | undefined): AuthScheme | undefined => {
  const named = headers ?? [];
  if (has(named, 'x-tenant-token'))
    return {
      id: 'docs-tenant-token',
      name: has(named, 'x-tenant-id')
        ? 'Tenant token in x-tenant-token, tenant in x-tenant-id'
        : 'Tenant token in x-tenant-token',
      keyHeader: 'x-tenant-token',
      ...(has(named, 'x-tenant-id') ? { tenantHeader: 'x-tenant-id' } : {}),
    };
  if (has(named, 'api-key') && has(named, 'authorization'))
    return {
      id: 'docs-api-key-or-authorization',
      name: 'Key in the api-key or Authorization header',
      keyHeader: 'api-key',
      fallbackKeyHeader: 'Authorization',
    };
  if (has(named, 'api-key')) return { id: 'docs-api-key', name: 'Key in the api-key header', keyHeader: 'api-key' };
  if (has(named, 'authorization'))
    return { id: 'docs-authorization', name: 'Key in the Authorization header', keyHeader: 'Authorization' };
  return undefined;
};

/** The sign-in most of a group's endpoints use, so only the odd one out carries its own. */
const usualSignIn = (schemeIds: (string | undefined)[]): string | undefined => {
  const counts = new Map<string, number>();
  for (const id of schemeIds) if (id) counts.set(id, (counts.get(id) ?? 0) + 1);

  return [...counts.entries()].sort((left, right) => right[1] - left[1])[0]?.[0];
};

/**
 * A catalogue as configuration.
 *
 * One group per folder or tag, one endpoint per documented request, and one target schema per
 * endpoint. The public address travels with the endpoint, because that is the whole point: a
 * vendor is already told to call `/v2/bills`, so that is the address this platform answers on.
 */
export const catalogImport = (groups: CatalogGroup[], version = 1): CatalogImport => {
  const schemas: CanonicalSchema[] = [];
  const authSchemes = new Map<string, AuthScheme>();

  const templates = groups.map((group) => {
    const signIns = group.endpoints.map((endpoint) => {
      const scheme = catalogSignIn(endpoint.auth);
      if (scheme) authSchemes.set(scheme.id, scheme);
      return scheme?.id;
    });
    const usual = usualSignIn(signIns);

    return {
      id: group.id,
      name: group.name,
      description: group.description,
      version,
      flowDir: group.id,
      ...(usual ? { authSchemeId: usual } : {}),
      members: group.endpoints.map((endpoint, index) => {
        const schema = catalogSchema(group, endpoint, version);
        if (schema.fields.length > 0) schemas.push(schema);
        const own = signIns[index];

        return {
          name: endpoint.name,
          slug: endpoint.slug,
          canonicalSchemaId: schema.fields.length > 0 ? schema.id : undefined,
          method: endpoint.method,
          path: endpoint.path,
          ...(endpoint.examples?.length ? { samples: endpoint.examples } : {}),
          isBatch: endpoint.isBatch,
          ...(own && own !== usual ? { authSchemeId: own } : {}),
          /* no header named: the key is in the address, and the group's sign-in would be wrong */
          ...(own ? {} : { signInInAddress: true }),
        };
      }),
    };
  });

  return { templates, schemas, authSchemes: [...authSchemes.values()] };
};

/**
 * Two readings of the same API, joined.
 *
 * One document names the variants and carries real payloads; the other types the fields. Neither
 * alone is enough, so a group that appears in both keeps the endpoints of each and prefers, per
 * endpoint, whichever reading actually has the piece in question.
 */
export const mergeCatalogs = (left: Catalog, right: Catalog): Catalog => {
  const groups = new Map(left.groups.map((group) => [group.id, { ...group, endpoints: [...group.endpoints] }]));

  for (const group of right.groups) {
    const held = groups.get(group.id);
    if (!held) {
      groups.set(group.id, { ...group, endpoints: [...group.endpoints] });
      continue;
    }

    for (const endpoint of group.endpoints) {
      const at = held.endpoints.findIndex(
        (candidate) => candidate.method === endpoint.method && candidate.path === endpoint.path,
      );
      if (at < 0) {
        held.endpoints.push(endpoint);
        continue;
      }
      const mine = held.endpoints[at];
      const examples = [...(mine.examples ?? [])];
      for (const example of endpoint.examples ?? [])
        if (!examples.some((item) => item.name === example.name)) examples.push(example);

      held.endpoints[at] = {
        ...mine,
        /* whichever reading has it: types come from one document, examples from the other */
        body: endpoint.body ?? mine.body,
        description: endpoint.description ?? mine.description,
        auth: endpoint.auth?.length ? endpoint.auth : mine.auth,
        isBatch: mine.isBatch ?? endpoint.isBatch,
        ...(examples.length ? { examples } : {}),
      };
    }
  }

  return { source: left.source, title: left.title ?? right.title, groups: [...groups.values()] };
};
